import type { Approval, EngagementStage, Finding, Scan } from '@ai-secure-twin/shared';

/*
 * Stage presentation lives here rather than in `mocks/data.ts`: these are labels for real
 * values from the shared enum, and the engagements pages now render live data.
 */

export const stageLabels: Record<EngagementStage, string> = {
  DRAFT: 'Draft',
  SCOPE_PENDING_APPROVAL: 'Scope awaiting approval',
  SCOPED: 'Scoped',
  DISCOVERY: 'Discovery',
  PASSIVE: 'Passive scan',
  PLAN_PENDING_APPROVAL: 'Plan awaiting approval',
  ACTIVE: 'Active scan',
  TRIAGE: 'Triage',
  REPORT_PENDING_APPROVAL: 'Report awaiting approval',
  PUBLISHED: 'Published',
  CANCELLED: 'Cancelled',
  FAILED: 'Failed',
};

/** Simplified stage track shown in the UI. */
export const stageTrack: { label: string; stages: EngagementStage[]; gate?: boolean }[] = [
  { label: 'Scope', stages: ['DRAFT', 'SCOPE_PENDING_APPROVAL', 'SCOPED'], gate: true },
  { label: 'Discover', stages: ['DISCOVERY'] },
  { label: 'Passive', stages: ['PASSIVE'] },
  { label: 'Active', stages: ['PLAN_PENDING_APPROVAL', 'ACTIVE'], gate: true },
  { label: 'Triage', stages: ['TRIAGE'] },
  { label: 'Report', stages: ['REPORT_PENDING_APPROVAL', 'PUBLISHED'], gate: true },
];

export function StageBadge({ stage }: { stage: EngagementStage }) {
  const cls = stage.endsWith('PENDING_APPROVAL')
    ? 'status-waiting'
    : stage === 'PUBLISHED' || stage === 'CANCELLED'
      ? 'status-done'
      : 'status-active';
  return <span className={`status ${cls}`}>{stageLabels[stage]}</span>;
}

/** What the track knows beyond the stage. Rows the detail page has already loaded. */
export interface TrackEvidence {
  scans: readonly Pick<Scan, 'status'>[];
  findings: readonly Pick<Finding, 'status'>[];
  approvals: readonly Pick<Approval, 'gate' | 'decision'>[];
  /** The engagement's current scope version, if it has one. */
  scopeVersion?: number;
  /** Whether the approved scope permits active scanning. Unknown is treated as no. */
  activeScanPermitted?: boolean;
}

/**
 * `skipped` is a stage the engagement went past without doing — active testing is optional, and
 * a track that filled it in anyway would claim attack payloads were sent when none were.
 */
export type SegmentState = 'done' | 'current' | 'skipped' | 'pending';

export interface Segment {
  label: string;
  gate: boolean;
  state: SegmentState;
  /** One short line of fact under the label: what happened in this stage, or what it waits on. */
  caption?: string;
}

const ACTIVE_INDEX = stageTrack.findIndex((s) => s.label === 'Active');

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * The track, worked out from the stage and the evidence.
 *
 * The stage decides which segment is current — it is what the server records and audits, and
 * the track never second-guesses it. The evidence only describes what happened inside each
 * stage, so the bar can say "1 scan · 13 findings" rather than merely "Passive".
 */
export function trackSegments(stage: EngagementStage, evidence: TrackEvidence): Segment[] {
  const current = stageTrack.findIndex((s) => s.stages.includes(stage));
  const published = stage === 'PUBLISHED';

  const planApproved = evidence.approvals.some(
    (a) => a.gate === 'PLAN' && a.decision === 'APPROVED',
  );
  const running = evidence.scans.some((s) => s.status === 'QUEUED' || s.status === 'RUNNING');
  const succeededCount = evidence.scans.filter((s) => s.status === 'SUCCEEDED').length;
  const findingCount = evidence.findings.length;
  const unverifiedCount = evidence.findings.filter((f) => f.status === 'UNVERIFIED').length;

  return stageTrack.map((segment, i): Segment => {
    let state: SegmentState =
      published || (current >= 0 && i < current) ? 'done' : i === current ? 'current' : 'pending';

    // Past the Active segment without an approved plan means it was never entered.
    if (i === ACTIVE_INDEX && state === 'done' && !planApproved) state = 'skipped';

    return {
      label: segment.label,
      gate: segment.gate ?? false,
      state,
      ...captionFor(segment.label, state, stage, {
        ...evidence,
        running,
        succeededCount,
        findingCount,
        unverifiedCount,
      }),
    };
  });
}

function captionFor(
  label: string,
  state: SegmentState,
  stage: EngagementStage,
  e: TrackEvidence & {
    running: boolean;
    succeededCount: number;
    findingCount: number;
    unverifiedCount: number;
  },
): { caption?: string } {
  const v = e.scopeVersion;

  switch (label) {
    case 'Scope':
      if (stage === 'DRAFT') return { caption: v ? `Draft v${v}` : 'No scope yet' };
      if (stage === 'SCOPE_PENDING_APPROVAL') return { caption: `v${v ?? '?'} awaiting approval` };
      return v ? { caption: `v${v} approved` } : {};

    case 'Discover':
      if (state === 'current') return { caption: 'Crawling the seed URLs' };
      return state === 'done' ? { caption: 'Seed URLs crawled' } : {};

    case 'Passive':
      if (state === 'pending') return {};
      if (e.running) return { caption: 'Scanning…' };
      if (e.succeededCount > 0) {
        return {
          caption: `${plural(e.succeededCount, 'scan')} · ${plural(e.findingCount, 'finding')}`,
        };
      }
      // Reachable: the stage advances when a scan starts, and a start can still fail.
      return { caption: e.scans.length > 0 ? 'Last scan failed — run again' : 'Not started' };

    case 'Active':
      if (stage === 'PLAN_PENDING_APPROVAL') return { caption: 'Plan awaiting approval' };
      if (stage === 'ACTIVE') return { caption: 'Active testing' };
      if (state === 'skipped') return { caption: 'Skipped' };
      if (state === 'done') return { caption: 'Plan approved' };
      return e.activeScanPermitted ? {} : { caption: 'Not permitted by scope' };

    case 'Triage':
      if (state === 'pending') return {};
      if (e.findingCount === 0) return { caption: 'Nothing to triage' };
      return {
        caption:
          e.unverifiedCount > 0
            ? `${e.unverifiedCount} of ${e.findingCount} unverified`
            : `All ${e.findingCount} judged`,
      };

    case 'Report':
      if (stage === 'REPORT_PENDING_APPROVAL') return { caption: 'Awaiting approval' };
      if (stage === 'PUBLISHED') return { caption: 'Published' };
      return {};

    default:
      return {};
  }
}

export function StageTrack({
  stage,
  evidence,
}: {
  stage: EngagementStage;
  evidence: TrackEvidence;
}) {
  const segments = trackSegments(stage, evidence);
  return (
    <ol
      className="stage-track"
      style={{ listStyle: 'none', margin: 0, padding: 0 }}
      aria-label="Engagement stages"
    >
      {segments.map((s) => (
        <li
          key={s.label}
          className={s.state}
          aria-current={s.state === 'current' ? 'step' : undefined}
        >
          <span className="bar" />
          <span>
            {s.label}
            {s.gate ? <span className="gate-mark">gate</span> : null}
          </span>
          {s.caption ? <span className="caption">{s.caption}</span> : null}
        </li>
      ))}
    </ol>
  );
}
